import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { setColumnWidth } from "../../../helpers/cookieUtils";
import ListingHeader from "../listingHeader/ListingHeader";
import {
  Tooltip,
  TableCellLayout,
  Popover,
  PopoverTrigger,
  PopoverSurface,
  makeStyles,
  AvatarGroupPopover,
  AvatarGroup,
  partitionAvatarGroupItems,
  Avatar,
} from "@fluentui/react-components";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import useNavigation from "../../../utils/useNavigation";
import {
  AttachementIcon,
  DeleteIcon,
  EditIcon,
  FlagIcon,
  MeatBallsMenuIcon,
  PendingDeleteIcon,
  RemindIcon,
  DismissCircleIcon,
  CheckmarkCircleIcon
} from "../../../modules/default-svg";
import {
  getAccountCurrency,
  dateOnlyFormatted,
  displayDecimalFormatted,
  isDateGreaterThan,
} from "../../../utils/systemSettings";
import userService from "../../../store/services/user.service";
import scopeService from "../../../store/services/scope.service";
import taskGroupService from "../../../store/services/taskGroup.service";
import CheckboxInput from "../checkbox/Checkbox";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import { generalActions } from "../../../store/actions";
import CustomSelect from "../select/CustomSelect";
import Spin from "../spinner/Spin";
import clientService from "../../../store/services/clients.service";
import { apiCall } from "../../../store/services/service";
import {
  changeRequestPriorities,
  compareArrays,
  getDeletePermission,
  formatSequenceNumber,
  getEditPermission,
  getCreatePermission,
  getLabelByType,
  ScopeStatus,
  sortColumnsByOrderlist,
} from "../../../helpers/utils";
import { BASE_URL } from "../../../store/services/URL";
import {
  createScopeRequest,
  updateScopeFlag,
  updateScopeRequest,
  updateTaskFlag,
} from "../../../store/actions/scope.actions";
import {
  isValidDate,
  localDateToISODateString,
} from "../../../utils/systemSettings";
import { Copy, Layers, Layers2, Plus, Target, TriangleAlert } from "lucide-react";
import { createSkeletonRows, TABLE_ACTIONS, useTableContextChange } from "./utils/tableUtils";
import { ROW_TYPE, buildScopeTreeFromBreakdown, buildScopeTreeFromSearchResults, compareByFlagAndStartDate } from "./utils/taskTree";
import { MilestoneIcon, UnActiveMilestoneIcon } from "../../../views/dashboard/icons/icons";
import CreateEditTask from "../../../views/scopeOverview/CreateEditTask";
import CreateEditTaskGroup from "../../../views/scopeOverview/CreateEditTaskGroup";
import Modal from "../modal/Modal";
import { ReminderModal as CreateReminder } from "../modal/reminder/Reminder";
import { getPortFolioProgressSummary } from "../../../store/actions/portfolio.actions";
import { getProgramProgressSummary } from "../../../store/actions/programs.actions";
import { getProjectProgressSummary } from "../../../store/actions/projects.actions";
import HoverCard from "../../hoverCard/HoverCard";

const getPriority = (priority) => {
  switch (priority) {
    case 0:
      return "Low";
    case 1:
      return "Moderate";
    case 2:
      return "High";
    default:
      return "";
  }
};

const getPriorityPill = (priority) => {
  switch (priority) {
    case 0:
      return "pill OnTrack";
    case 1:
      return "pill OffTrack";
    case 2:
      return "pill AtRisk";
    default:
      return "";
  }
};

const setRequestPriority = (type) => {
  switch (type) {
    case "Low":
      return 0;
    case "Moderate":
      return 1;
    case "High":
      return 2;
    default:
      return 0;
  }
};

// Task rows use a different status enum/labels than Scope rows.
const getTaskStatusPill = (status) => {
  switch (status) {
    case 0:
      return "pill OnTrack";
    case 1:
      return "pill OffTrack";
    case 2:
      return "pill AtRisk";
    case 3:
      return "pill Completed";
    case 4:
      return "pill OnHold";
    default:
      return "";
  }
};

const getTaskStatus = (status) => {
  switch (status) {
    case 1:
      return "Monitoring";
    case 2:
      return "Slipping";
    case 3:
      return "Completed";
    case 4:
      return "On Hold";
    default:
      return "In Progress";
  }
};

const createChildSkeletonRows = (scopeId) =>
  [0, 1].map((i) => ({ id: `skeleton-${scopeId}-${i}`, isSkeleton: true }));

const hasOpenRisk = (entity) =>
  !!entity?.risks?.length && entity.risks.some((r) => r.status !== 5);

const EditInput = ({
  label,
  value,
  onChange,
  type,
  onBlur,
  dateFormat,
  clientId,
  programId,
  portfolioId,
  parentType,
  table,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [resources, setResources] = useState([]);
  const [clients, setClients] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]);
  const [dropdownOptions, setDropdownOptions] = useState([]);

  const formatValue = (val) => {
    if (!val) return null;
    if (val.label && val.value) return val;
    return { label: val.name, value: val.id };
  };

  const handleDateChange = (newDate) => {
    if (isValidDate(newDate)) {
      const newValue = localDateToISODateString(newDate);
      setInputValue(newValue);
      onChange(newValue);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const creatingRowState = table.getState().creatingRow;
      if (table?.options?.onCreatingRowSave && creatingRowState) {
        const currentValues = {
          ...creatingRowState.values,
        };
        table.options.onCreatingRowSave({
          values: currentValues,
          table,
        });
      }
    }
  };

  const handleSelectChange = (item) => {
    if (!item) return;
    const newValue = { id: item.value, name: item.label };
    handleInputChange(newValue);
  };

  const handleInputChange = (newValue) => {
    setInputValue(newValue);
    if (onChange) {
      onChange(newValue);
    }
  };

  const searchHandler = (value) => {
    if (value === "") {
      setResources([]);
      return;
    }
    userService
      .searchUserDataRequest(value)
      .then((res) => {
        const transformedArray = res.map((obj) => ({
          label: obj.name,
          value: obj.id,
        }));
        setResources(transformedArray);
      })
      .catch(() => {
        setResources([]);
      });
  };

  const searchClientHandler = (value) => {
    if (value === "") {
      setClients([]);
      return;
    }
    clientService
      .searchClientsRequest(value)
      .then((res) => {
        const transformedArray = res.map((obj) => ({
          label: obj.name,
          value: obj.id,
        }));
        setClients(transformedArray);
      })
      .catch(() => {
        setClients([]);
      });
  };

  const searchProjectHandler = (value) => {
    if (value === "") {
      setProjectOptions([]);
      return;
    }
    const payload = { searchText: value };
    let queryParam = `?searchTerm=${payload.searchText}&clientId=${clientId}`;
    payload.parent = "Budget";

    if (parentType === "Program") {
      queryParam = queryParam + `&programId=${programId}`;
    } else if (parentType === "Portfolio") {
      queryParam = queryParam + `&portfolioId=${portfolioId}`;
    }

    apiCall
      .get({ url: `${BASE_URL}search/Projects${queryParam}` })
      .then((res) => {
        const transformedArray = res.map((obj) => ({
          start: obj.startDate,
          end: obj.endDate,
          label: obj.name,
          value: obj.id,
        }));
        setProjectOptions(transformedArray);
      })
      .catch(() => {
        setProjectOptions([]);
      });
  };

  const dropdownHandler = () => {
    if (label === "Status") {
      setDropdownOptions(() =>
        ScopeStatus.map((obj) => ({
          label: obj.name,
          value: obj.value,
        }))
      );
    } else if (label === "Priority") {
      setDropdownOptions(() =>
        changeRequestPriorities.map((obj) => ({
          label: obj.name,
          value: obj.value,
        }))
      );
    }
  };

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  if (type === "text") {
    return (
      <input
        placeholder={`Enter ${label}`}
        defaultValue={inputValue}
        type="text"
        onChange={(e) => handleInputChange(e.target.value)}
        className="Custom-Input"
        onBlur={onBlur}
        onKeyDown={handleKeyDown}
      />
    );
  }

  if (type === "number") {
    return (
      <input
        placeholder={`Enter ${label}`}
        defaultValue={inputValue}
        type="number"
        min={0}
        onChange={(e) => handleInputChange(e.target.value)}
        className="Custom-Input"
        onBlur={onBlur}
        onKeyDown={handleKeyDown}
      />
    );
  }

  if (type === "select") {
    let searchTextFunc = searchHandler;
    let options = resources;
    let selectText = "manager";
    let isCreatable = true;
    if (label === "Client") {
      searchTextFunc = searchClientHandler;
      options = clients;
      selectText = "client";
    } else if (label === "Project") {
      searchTextFunc = searchProjectHandler;
      options = projectOptions;
      selectText = "project";
    } else if (label === "Status") {
      searchTextFunc = dropdownHandler;
      options = dropdownOptions;
      selectText = "status";
      isCreatable = false;
    } else if (label === "Priority") {
      searchTextFunc = dropdownHandler;
      options = dropdownOptions;
      selectText = "status";
      isCreatable = false;
    }

    return (
      <div className="d-flex align-items-center w-full max-w-md mx-auto">
        <CustomSelect
          searchText={searchTextFunc}
          SelectText={selectText}
          CustomClass="ModalAvatarSearch CustomSelect"
          options={options}
          value={formatValue(value)}
          onChange={handleSelectChange}
          creatable={isCreatable}
          setCreated={handleSelectChange}
          handleBlur={onBlur}
          isDatatable
          selectWd="w-100"
          onKeyDown={handleKeyDown}
        />
      </div>
    );
  }

  return (
    <DatePicker
      selected={inputValue ? moment(inputValue).toDate() : null}
      onChange={handleDateChange}
      dateFormat={dateFormat}
      className="CustomDatePickerInput"
      onBlur={onBlur}
      popperPlacement="bottom-start"
      placeholderText={dateFormat}
      onKeyDown={handleKeyDown}
      popperClassName="CustomDatePickerPopper"
      showPopperArrow={false}
    />
  );
};

const useStyles = makeStyles({
  tooltip: {
    backgroundColor: "#342c82",
    color: "#FFFFFF",
  },
});

const ScopeTable = (props) => {
  const {
    columnsList,
    rowData,
    setIsCreate,
    searchHandler,
    tableName,
    handleColumnOrdering,
    dispatch,
    setPageNum,
    perPageItems,
    pagination,
    handleReminder,
    handleEdit,
    handleDelete,
    parentType,
    portfolioId,
    programId,
    projectId,
    scopeLoading,
    handleAddRow,
    handleCancelAddRow,
    setRowData,
    isHeader,
    isMilestones = false,
    triggerSearch,
    handleAction
  } = props;
  const styles = useStyles();
  const [cols, setCols] = useState([]);
  const [rows, setRows] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const setting = JSON.parse(localStorage.getItem("settings"));
  const [editedRows, setEditedRows] = useState({});
  const tableContainerRef = useRef(null);
  const lastScrollTop = useRef(0);
  const selectRefs = useRef({});
  const { navigateTo } = useNavigation();
  const [editingHeader, setEditingHeader] = useState(null);
  const [headerValue, setHeaderValue] = useState("");
  const [columnSizing, setColumnSizing] = useState({});
  const [columnOrder, setColumnOrder] = useState([]);
  const [sorting, setSorting] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [isAllRowsExpanded, setIsAllRowsExpanded] = useState(false);
  const [popoverStates, setPopoverStates] = useState({});
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  // Per-scope task group + task tree state. Keyed by scope id, populated lazily
  // on expand (see loadScopeChildren/fetchScopeChildren below).
  const [scopeChildren, setScopeChildren] = useState({});
  const [loadingScopeChildrenIds, setLoadingScopeChildrenIds] = useState(
    () => new Set()
  );

  // Cross-entity search results (see handleTriggerSearch below). Non-null
  // while the search box has a value; each entry is a fully self-contained,
  // already-expanded scope row built from ScopeSearchBreakdownDto.
  const [searchTreeRows, setSearchTreeRows] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  // Tracks the current search box value so mutations (create/edit/delete)
  // can re-run the search and keep searchTreeRows in sync (see refreshScopeTree).
  const [activeSearchTerm, setActiveSearchTerm] = useState("");

  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [taskData, setTaskData] = useState(null);
  const [isEditTaskModal, setIsEditTaskModal] = useState(false);
  const [createTaskContext, setCreateTaskContext] = useState(null);
  const [isDeleteTaskModal, setIsDeleteTaskModal] = useState(false);
  const [isDuplicateTaskModal, setIsDuplicateTaskModal] = useState(false);
  const [isReminderTaskModal, setIsReminderTaskModal] = useState(false);
  const [isDeletingTask, setIsDeletingTask] = useState(false);
  const [isDuplicatingTask, setIsDuplicatingTask] = useState(false);

  const [isTaskGroupModalOpen, setIsTaskGroupModalOpen] = useState(false);
  const [taskGroupData, setTaskGroupData] = useState(null);
  const [isEditTaskGroup, setIsEditTaskGroup] = useState(false);
  const [isDeleteTaskGroupModal, setIsDeleteTaskGroupModal] = useState(false);
  const [isDeletingTaskGroup, setIsDeletingTaskGroup] = useState(false);
  const [isDuplicateTaskGroupModal, setIsDuplicateTaskGroupModal] = useState(false);
  const [isDuplicatingTaskGroup, setIsDuplicatingTaskGroup] = useState(false);
  const [duplicateTaskGroupIncludeTasks, setDuplicateTaskGroupIncludeTasks] = useState(false);
  const [groupTasksScopeId, setGroupTasksScopeId] = useState(null);
  const [groupTasksInitialTasks, setGroupTasksInitialTasks] = useState([]);

  // Task selection, driving the "Group Tasks" toolbar action.
  const [selectedTasks, setSelectedTasks] = useState(() => new Map());
  const selectedTaskList = useMemo(() => Array.from(selectedTasks.values()), [selectedTasks]);
  const selectedTaskScopeIds = useMemo(
    () => new Set(selectedTaskList.map((t) => t.scopeId)),
    [selectedTaskList]
  );
  const canGroupSelectedTasks = selectedTaskList.length > 0 && selectedTaskScopeIds.size === 1;

  const toggleTaskSelection = (item, checked) => {
    setSelectedTasks((prev) => {
      const next = new Map(prev);
      if (checked) {
        next.set(item.id, { id: item.id, name: item.name, scopeId: item.__scopeId });
      } else {
        next.delete(item.id);
      }
      return next;
    });
  };

  const { hasContextChanged } = useTableContextChange({
    contextValues: {
      portfolioId,
      programId,
      projectId,
      parentType,
    },
    setIsFetchingMore,
    setRows,
    setRowData,
  });

  const handlePopoverChange = (itemId, isOpen) => {
    setPopoverStates(isOpen ? { [itemId]: isOpen } : {});
  };
  const [colsList, setColumnsList] = useState([]);
  useEffect(() => {
    if (columnsList.length) {
      setColumnsList(columnsList);
    }
  }, [columnsList]);
  const handleHeaderDoubleClick = (columnId, currentHeader) => {
    setEditingHeader(columnId);
    setHeaderValue(currentHeader);
  };

  const handleHeaderSave = (columnId) => {
    if (!headerValue.trim()) {
      dispatch(
        generalActions.showToast({
          title: "Header name cannot be empty",
          type: "error",
        })
      );
      return;
    }

    const updatedColumnsDB = cols.map((col) => ({
      name: col.Label,
      customName: col.accessorKey === columnId ? headerValue : col.CustomName,
      order: col.Order,
      isVisible: col.Visibility,
    }));

    const payload = {
      Name: tableName,
      Columns: updatedColumnsDB,
    };

    userService
      .putUserLayoutRequest(payload)
      .then(() => { })
      .catch(() => {
        dispatch(
          generalActions.showToast({
            title: "Error updating column header",
            type: "error",
          })
        );
      })
      .finally(() => {
        setColumnsList((prevCols) => {
          const newCols = prevCols.map((col) =>
            col.accessorKey === columnId
              ? { ...col, header: headerValue, customName: headerValue }
              : col
          );
          return newCols;
        });
      });

    setEditingHeader(null);
    setHeaderValue("");
  };

  const handleRedirect = (item) => {
    const entityType = isMilestones ? "Milestones" : "ScopeItems";
    navigateTo(entityType, item.id, item.name);
  };

  const handleProjectRedirect = (item) => {
    if (item.project) {
      navigateTo("Projects", item.project.id, item.project.name);
    }
  };

  const GetItemSequence = (rowType, sequenceNumber) => {
    switch (rowType) {
      case ROW_TYPE.TASK:
        return formatSequenceNumber("Task", sequenceNumber);
      case ROW_TYPE.TASK_GROUP:
        return formatSequenceNumber("TaskGroup", sequenceNumber);
      default:
        return formatSequenceNumber("Scope", sequenceNumber);

    }
  };

  const handleFlag = (id, status) => {
    const newData = [...rowData];
    const index = newData.findIndex((item) => item.id === id);
    if (index !== -1) {
      newData[index].isFlagged = status;
    }

    setRowData(newData.sort(compareByFlagAndStartDate));
    dispatch(
      updateScopeFlag({
        id: id,
        payload: [
          {
            op: "replace",
            path: "/IsFlagged",
            value: status,
          },
        ],
      })
    );
  };

  const handleToggleField = (id, field, newValue) => {
    const prevData = [...rowData];
    const newData = [...rowData];
    const index = newData.findIndex((item) => item.id === id);
    if (index === -1) return;
    newData[index] = { ...newData[index], [field]: newValue };
    setRowData(newData);

    scopeService
      .patchScopeField(id, field, newValue)
      .catch(() => {
        setRowData(prevData);
        dispatch(
          generalActions.showToast({
            title: `Failed to update Scope`,
            type: "error",
          })
        );
      });
  };

  const handleToggleMilestone = (id, newValue) => {
    const prevData = [...rowData];
    const newData = [...rowData];
    const index = newData.findIndex((item) => item.id === id);
    if (index === -1) return;
    newData[index] = { ...newData[index], isMilestone: newValue };
    setRowData(newData);

    scopeService
      .updateScopeMilestoneStatus(id, newValue)
      .catch(() => {
        setRowData(prevData);
        dispatch(
          generalActions.showToast({
            title: "Failed to update Milestone status",
            type: "error",
          })
        );
      });
  };

  // ---- Task group / task tree helpers -------------------------------------

  const handleProgressRefresh = () => {
    if (projectId) {
      dispatch(getProjectProgressSummary({ id: projectId }));
      return;
    }
    if (programId) {
      dispatch(getProgramProgressSummary({ id: programId }));
      return;
    }
    if (portfolioId) {
      dispatch(getPortFolioProgressSummary({ id: portfolioId }));
    }
  };

  const stampScopeOwnership = (subRows, scopeId, scopeName) =>
    subRows.map((row) => ({
      ...row,
      __scopeId: scopeId,
      __scopeName: scopeName,
      subRows: row.subRows?.map((child) => ({
        ...child,
        __scopeId: scopeId,
        __scopeName: scopeName,
      })),
    }));

  const fetchScopeChildren = useCallback(
    (scopeId) => {
      setLoadingScopeChildrenIds((prev) => new Set(prev).add(scopeId));
      return scopeService
        .getScopeTasksBreakdownRequest({ scopeId })
        .then((breakdown) => {
          const scopeName = breakdown.scope?.name || rows.find((r) => r.id === scopeId)?.name || "";
          const subRows = stampScopeOwnership(
            buildScopeTreeFromBreakdown(breakdown),
            scopeId,
            scopeName
          );
          setScopeChildren((prev) => ({ ...prev, [scopeId]: subRows }));

          // Editing a group/task can change the scope's own Progress/dates
          // server-side (recalculation) - refresh this scope's row from the
          // full Scope object the breakdown now carries, not just its children.
          if (breakdown.scope) {
            const newData = [...rowData];
            const index = newData.findIndex((item) => item.id === scopeId);
            if (index !== -1) {
              newData[index] = { ...newData[index], ...breakdown.scope };
              setRowData(newData);
            }
          }
        })
        .catch(() => {
          dispatch(
            generalActions.showToast({
              title: "Failed to fetch scope tasks",
              type: "error",
            })
          );
        })
        .finally(() => {
          setLoadingScopeChildrenIds((prev) => {
            const next = new Set(prev);
            next.delete(scopeId);
            return next;
          });
        });
    },
    [rows, rowData, setRowData, dispatch]
  );

  const loadScopeChildren = useCallback(
    (scopeId) => {
      if (scopeChildren[scopeId] || loadingScopeChildrenIds.has(scopeId)) {
        return Promise.resolve();
      }
      return fetchScopeChildren(scopeId);
    },
    [scopeChildren, loadingScopeChildrenIds, fetchScopeChildren]
  );

  const loadAllScopeChildren = useCallback(() => {
    const scopeIds = new Set(rows.map((r) => r.id));
    if (!scopeIds.size) return Promise.resolve();
    scopeIds.forEach((id) =>
      setLoadingScopeChildrenIds((prev) => new Set(prev).add(id))
    );
    return scopeService
      .getAllScopesTasksBreakdownRequest({
        portfolioId,
        programId,
        projectId,
        onlyMilestones: isMilestones,
      })
      .then((breakdowns) => {
        const scopeNameById = new Map(rows.map((r) => [r.id, r.name]));
        const next = {};
        (breakdowns || []).forEach((breakdown) => {
          const scopeId = breakdown.scope?.id;
          if (!scopeId) return;
          next[scopeId] = stampScopeOwnership(
            buildScopeTreeFromBreakdown(breakdown),
            scopeId,
            breakdown.scope?.name || scopeNameById.get(scopeId) || ""
          );
        });
        setScopeChildren((prev) => ({ ...prev, ...next }));
      })
      .catch(() => {
        dispatch(
          generalActions.showToast({
            title: "Failed to fetch scope tasks",
            type: "error",
          })
        );
      })
      .finally(() => {
        setLoadingScopeChildrenIds((prev) => {
          const next = new Set(prev);
          scopeIds.forEach((id) => next.delete(id));
          return next;
        });
      });
  }, [rows, portfolioId, programId, projectId, isMilestones, dispatch]);

  const handleTriggerSearch = useCallback(
    (term) => {
      setActiveSearchTerm(term || "");
      if (!term) {
        setSearchTreeRows(null);
        return Promise.resolve();
      }
      setIsSearching(true);
      return scopeService
        .searchScopeTasksBreakdownRequest({
          searchTerm: term,
          portfolioId,
          programId,
          projectId,
        })
        .then((results) => {
          const treeRows = buildScopeTreeFromSearchResults(results).map(
            (scopeRow) => ({
              ...scopeRow,
              subRows: stampScopeOwnership(
                scopeRow.subRows,
                scopeRow.id,
                scopeRow.name
              ),
            })
          );
          setSearchTreeRows(treeRows);
        })
        .catch(() => {
          setSearchTreeRows([]);
          dispatch(
            generalActions.showToast({
              title: "Failed to search scopes",
              type: "error",
            })
          );
        })
        .finally(() => {
          setIsSearching(false);
        });
    },
    [portfolioId, programId, projectId, dispatch]
  );

  // Refreshes the tree after a create/edit/delete mutation. Always reloads the
  // owning scope's lazy-loaded children, and — since treeData renders
  // searchTreeRows exclusively while a search is active (see treeData below) —
  // also re-runs the active search so mutated rows show up there too.
  const refreshScopeTree = useCallback(
    (scopeId) => {
      const tasks = [];
      if (scopeId) tasks.push(fetchScopeChildren(scopeId));
      if (activeSearchTerm) tasks.push(handleTriggerSearch(activeSearchTerm));
      return Promise.all(tasks);
    },
    [activeSearchTerm, handleTriggerSearch, fetchScopeChildren]
  );

  const updateChildRow = (scopeId, rowId, updater) => {
    if (!scopeId) return;
    setScopeChildren((prev) => {
      const list = prev[scopeId];
      if (!list) return prev;
      const apply = (items) =>
        items.map((r) => {
          if (r.id === rowId) return updater(r);
          if (r.subRows) return { ...r, subRows: apply(r.subRows) };
          return r;
        });
      return { ...prev, [scopeId]: apply(list) };
    });
  };

  // Flagging a row moves it to the top of its own sibling list (flagged
  // first, then start date): a task inside a group reorders within that
  // group's tasks; a top-level row (group or ungrouped task) reorders within
  // its own block, mirroring how the top-level Scope list already sorts.
  const updateAndReorderByFlag = (scopeId, rowId, newFlagValue) => {
    if (!scopeId) return;
    setScopeChildren((prev) => {
      const list = prev[scopeId];
      if (!list) return prev;

      const groupIndex = list.findIndex(
        (r) => r.rowType === ROW_TYPE.TASK_GROUP && r.subRows?.some((t) => t.id === rowId)
      );
      if (groupIndex !== -1) {
        const group = list[groupIndex];
        const updatedSubRows = group.subRows
          .map((t) => (t.id === rowId ? { ...t, isFlagged: newFlagValue } : t))
          .sort(compareByFlagAndStartDate);
        const newList = [...list];
        newList[groupIndex] = { ...group, subRows: updatedSubRows };
        return { ...prev, [scopeId]: newList };
      }

      const groups = list
        .filter((r) => r.rowType === ROW_TYPE.TASK_GROUP)
        .map((r) => (r.id === rowId ? { ...r, isFlagged: newFlagValue } : r))
        .sort(compareByFlagAndStartDate);
      const tasks = list
        .filter((r) => r.rowType !== ROW_TYPE.TASK_GROUP)
        .map((r) => (r.id === rowId ? { ...r, isFlagged: newFlagValue } : r))
        .sort(compareByFlagAndStartDate);
      return { ...prev, [scopeId]: [...groups, ...tasks] };
    });
  };

  const handleTaskFlag = (item, status) => {
    updateAndReorderByFlag(item.__scopeId, item.id, status);
    dispatch(
      updateTaskFlag({
        id: item.id,
        payload: [{ op: "replace", path: "/IsFlagged", value: status }],
      })
    );
  };

  const handleTaskGroupFlag = (item, status) => {
    updateAndReorderByFlag(item.__scopeId, item.id, status);
    taskGroupService.patchTaskGroupField(item.id, "IsFlagged", status).catch(() => {
      dispatch(
        generalActions.showToast({
          title: "Failed to update flag",
          type: "error",
        })
      );
      if (item.__scopeId) refreshScopeTree(item.__scopeId);
    });
  };

  const handleTaskToggleMilestone = (item, newValue) => {
    updateChildRow(item.__scopeId, item.id, (r) => ({ ...r, isMilestone: newValue }));
    scopeService.updateTaskMilestoneStatus(item.id, newValue).catch(() => {
      dispatch(
        generalActions.showToast({
          title: "Failed to update Milestone status",
          type: "error",
        })
      );
      if (item.__scopeId) refreshScopeTree(item.__scopeId);
    });
  };

  const handleTaskToggleWsr = (item, newValue) => {
    updateChildRow(item.__scopeId, item.id, (r) => ({ ...r, isInWsr: newValue }));
    scopeService.patchTaskField(item.id, "IsInWsr", newValue).catch(() => {
      dispatch(
        generalActions.showToast({
          title: "Failed to update In WSR status",
          type: "error",
        })
      );
      if (item.__scopeId) refreshScopeTree(item.__scopeId);
    });
  };

  const closeTaskModal = () => {
    setIsTaskModalOpen(false);
    setTaskData(null);
    setIsEditTaskModal(false);
    setCreateTaskContext(null);
  };

  const handleTaskEdit = (item) => {
    scopeService
      .getTaskDetails({ id: item.id })
      .then((detail) => {
        setTaskData({
          ...detail,
          __scopeId: item.__scopeId,
          __scopeName: item.__scopeName,
        });
        setIsEditTaskModal(true);
        setIsTaskModalOpen(true);
      })
      .catch(() => {
        dispatch(
          generalActions.showToast({
            title: "Something went wrong. Please try again later.",
            type: "error",
          })
        );
      });
  };

  const handleTaskDeleteConfirm = (task) => {
    setIsDeletingTask(true);
    scopeService
      .deleteTaskRequest({ id: task.id })
      .then(() => {
        dispatch(
          generalActions.showToast({
            title: "Task deleted successfully.",
            type: "success",
          })
        );
        handleProgressRefresh();
      })
      .catch(() => {
        dispatch(
          generalActions.showToast({
            title: "Something went wrong. Please try again later.",
            type: "error",
          })
        );
      })
      .finally(() => {
        setIsDeletingTask(false);
        setIsDeleteTaskModal(false);
        if (task.__scopeId) refreshScopeTree(task.__scopeId);
        setTaskData(null);
      });
  };

  const handleTaskDuplicateConfirm = (task) => {
    setIsDuplicatingTask(true);
    scopeService
      .duplicateTaskRequest({ id: task.id })
      .then(() => {
        handleProgressRefresh();
      })
      .catch(() => {
        dispatch(
          generalActions.showToast({
            title: "Something went wrong. Please try again later.",
            type: "error",
          })
        );
      })
      .finally(() => {
        setIsDuplicatingTask(false);
        setIsDuplicateTaskModal(false);
        if (task.__scopeId) refreshScopeTree(task.__scopeId);
        setTaskData(null);
      });
  };

  const closeTaskGroupModal = () => {
    setIsTaskGroupModalOpen(false);
    setTaskGroupData(null);
    setIsEditTaskGroup(false);
    setGroupTasksScopeId(null);
    setGroupTasksInitialTasks([]);
  };

  const handleTaskGroupEdit = (item) => {
    setTaskGroupData(item);
    setIsEditTaskGroup(true);
    setIsTaskGroupModalOpen(true);
  };

  const handleTaskGroupCreate = () => {
    setTaskGroupData(null);
    setIsEditTaskGroup(false);
    setGroupTasksScopeId(null);
    setGroupTasksInitialTasks([]);
    setIsTaskGroupModalOpen(true);
  };

  const handleGroupSelectedTasks = () => {
    if (!canGroupSelectedTasks) return;
    setTaskGroupData(null);
    setIsEditTaskGroup(false);
    setGroupTasksScopeId([...selectedTaskScopeIds][0]);
    setGroupTasksInitialTasks(selectedTaskList.map((t) => ({ id: t.id, name: t.name })));
    setIsTaskGroupModalOpen(true);
  };

  const handleScopeAddTaskGroup = (scopeRow) => {
    setTaskGroupData(null);
    setIsEditTaskGroup(false);
    setGroupTasksScopeId(scopeRow.id);
    setGroupTasksInitialTasks([]);
    setIsTaskGroupModalOpen(true);
  };

  const handleScopeAddTask = (scopeRow) => {
    setTaskData(null);
    setIsEditTaskModal(false);
    setCreateTaskContext({ scopeId: scopeRow.id, scopeName: scopeRow.name, taskGroup: null });
    setIsTaskModalOpen(true);
  };

  const handleTaskGroupAddTask = (groupRow) => {
    setTaskData(null);
    setIsEditTaskModal(false);
    setCreateTaskContext({
      scopeId: groupRow.__scopeId,
      scopeName: groupRow.__scopeName,
      taskGroup: { label: groupRow.name, value: groupRow.id },
    });
    setIsTaskModalOpen(true);
  };

  const handleTaskGroupDeleteConfirm = (group) => {
    setIsDeletingTaskGroup(true);
    taskGroupService
      .deleteTaskGroupRequest({ id: group.id })
      .then(() => {
        dispatch(
          generalActions.showToast({
            title: "Task group deleted successfully.",
            type: "success",
          })
        );
      })
      .catch(() => {
        dispatch(
          generalActions.showToast({
            title: "Something went wrong. Please try again later.",
            type: "error",
          })
        );
      })
      .finally(() => {
        setIsDeletingTaskGroup(false);
        setIsDeleteTaskGroupModal(false);
        if (group.__scopeId) refreshScopeTree(group.__scopeId);
        setTaskGroupData(null);
      });
  };

  const handleTaskGroupDuplicate = (e, item, isIncludeTasks = false) => {
    e.stopPropagation();
    e.preventDefault();
    handlePopoverChange(item.id, !popoverStates[item.id]);
    setTaskGroupData(item);
    setDuplicateTaskGroupIncludeTasks(isIncludeTasks);
    setIsDuplicateTaskGroupModal(true);
  };

  const handleTaskGroupDuplicateConfirm = () => {
    if (!taskGroupData) return;
    setIsDuplicatingTaskGroup(true);
    taskGroupService
      .duplicateTaskGroupRequest({
        id: taskGroupData.id,
        payload: { IsIncludeTasks: duplicateTaskGroupIncludeTasks },
      })
      .then(() => {
        dispatch(
          generalActions.showToast({
            title: "Task group duplicated successfully.",
            type: "success",
          })
        );
      })
      .catch(() => {
        dispatch(
          generalActions.showToast({
            title: "Something went wrong. Please try again later.",
            type: "error",
          })
        );
      })
      .finally(() => {
        setIsDuplicatingTaskGroup(false);
        setIsDuplicateTaskGroupModal(false);
        if (taskGroupData.__scopeId) refreshScopeTree(taskGroupData.__scopeId);
        setTaskGroupData(null);
      });
  };

  const fetchMoreData = useCallback(() => {
    if (!pagination?.HasNext || scopeLoading || isFetchingMore || hasContextChanged || searchTreeRows) return;
    setIsFetchingMore(true);
    setPageNum(pagination.CurrentPage + 1);
  }, [pagination, scopeLoading, isFetchingMore, setPageNum, hasContextChanged, searchTreeRows]);

  useEffect(() => {
    if (!scopeLoading && isFetchingMore) {
      setIsFetchingMore(false);
    }
  }, [scopeLoading, rowData.length]);

  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement) => {
      if (!containerRefElement || scopeLoading || !pagination?.HasNext) return;

      const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
      const scrolledToBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 50;
      const isScrollingDown = scrollTop > lastScrollTop.current;

      lastScrollTop.current = scrollTop;

      if (isScrollingDown && scrolledToBottom) {
        fetchMoreData();
      }
    },
    [fetchMoreData, scopeLoading, pagination]
  );

  const throttledFetchMore = useCallback(
    (() => {
      let timeoutId;
      return (containerRefElement) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          fetchMoreOnBottomReached(containerRefElement);
        }, 100);
      };
    })(),
    [fetchMoreOnBottomReached]
  );

  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      const handleScroll = () => throttledFetchMore(container);
      container.addEventListener("scroll", handleScroll, { passive: true });
      return () => {
        container.removeEventListener("scroll", handleScroll);
      };
    }
  }, [throttledFetchMore]);

  useEffect(() => {
    if (tableContainerRef.current && triggerSearch !== undefined) {
      tableContainerRef.current.scrollTop = 0;
      lastScrollTop.current = 0;
    }
  }, [triggerSearch]);

  const formatPayload = (payload) => {
    const formattedPayload = {};
    Object.keys(payload).forEach((key) => {
      formattedPayload[key] = payload[key];
    });
    return formattedPayload;
  };

  const getResources = (rowType, item) => {

    let resources = [];


    switch (rowType) {
      case ROW_TYPE.TASK:
        resources = item.allocations;
        break;
      case ROW_TYPE.TASK_GROUP:
        resources = [
          ...new Map(
            item.subRows
              .flatMap(row => row.allocations ?? [])
              .map(allocation => [allocation.resource.id, allocation])
          ).values(),

        ];
        break;
    }

    if (!resources || resources?.length === 0)
      return "";

    const { inlineItems, overflowItems } = partitionAvatarGroupItems({
      items: resources,
    });
    return (
      <AvatarGroup layout="stack" className="listingAvatarGroup" size={20}>
        {inlineItems.map((item) => (
          <HoverCard
            HoverCardTrigger={
              <Avatar
                color="colorful"
                size={20}
                name={item.resource.name}
                image={{ src: item.resource.image }}
                key={item.resource.name}
              />
            }

            AvatarId={item.resource?.id}
            AvatarName={item.resource?.name}
            AvatarImage={item.resource?.image}
            AvatarDesignation={item.resource?.title || ""}
            AvatarEmail={item.resource?.email}
            AvatarPhone={item.resource?.phoneNumber}
            AvatarIsExternal={item.resource?.isExternal}
          />
        ))}
        {overflowItems && (
          <AvatarGroupPopover>
            {overflowItems.map((item) => (
              <div className="d-flex inline-items gap-2">
                <HoverCard
                  HoverCardTrigger={
                    <Avatar
                      color="colorful"
                      size={20}
                      name={item.resource.name}
                      image={{ src: item.resource.image }}
                      key={item.resource.name}
                    />
                  }
                  AvatarId={item.resource?.id}
                  AvatarName={item.resource?.name}
                  AvatarImage={item.resource?.image}
                  AvatarDesignation={item.resource?.title || ""}
                  AvatarEmail={item.resource?.email}
                  AvatarPhone={item.resource?.phoneNumber}
                  AvatarIsExternal={item.resource?.isExternal}
                />
                <p className="font-figtree text-[13px]">{item.resource.name}</p>
              </div>
            ))}
          </AvatarGroupPopover>
        )}
      </AvatarGroup>
    );
  };
  const handleSave = (rowId, payload) => {
    if (projectId) {
      dispatch(
        updateScopeRequest({
          sId: rowId,
          parentID: projectId,
          parent: "Project",
          payload,
        })
      );
      return;
    }
    if (portfolioId) {
      dispatch(
        updateScopeRequest({
          sId: rowId,
          parentID: portfolioId,
          parent: "Portfolio",
          payload,
        })
      );
      return;
    }
    if (programId) {
      dispatch(
        updateScopeRequest({
          sId: rowId,
          parentID: programId,
          parent: "Program",
          payload,
        })
      );
      return;
    }

    dispatch(updateScopeRequest({ sId: rowId, parent: "Scope", payload }));
    setEditedRows({});
    return;
  };

  const handleCreate = (payload) => {
    if (projectId) {
      dispatch(
        createScopeRequest({
          parentID: projectId,
          parent: "Project",
          payload,
          perPageItems,
        })
      );
      return;
    }
    if (programId) {
      dispatch(
        createScopeRequest({
          parentID: programId,
          parent: "Program",
          payload,
          perPageItems,
        })
      );
      return;
    }
    if (portfolioId) {
      dispatch(
        createScopeRequest({
          parentID: portfolioId,
          parent: "Portfolio",
          payload,
          perPageItems,
        })
      );
      return;
    }

    dispatch(createScopeRequest({ parent: "Scope", payload, perPageItems }));
  };

  const getSortingFunction = (col) => {
    switch (col.label) {
      case "Scope":
        return (rowA, rowB, columnId) => {
          const a = (rowA.original.name || "").toLowerCase();
          const b = (rowB.original.name || "").toLowerCase();
          return a.localeCompare(b);
        };
      case "Sequence #":
        return (rowA, rowB, columnId) => {
          const a = rowA.original.sequenceNumber || 0;
          const b = rowB.original.sequenceNumber || 0;
          return a - b;
        };
      case "Project":
        return (rowA, rowB, columnId) => {
          const a = (rowA.original.project?.name || "").toLowerCase();
          const b = (rowB.original.project?.name || "").toLowerCase();
          return a.localeCompare(b);
        };
      case "Phase":
        return (rowA, rowB, columnId) => {
          const a = (rowA.original.phase || "").toLowerCase();
          const b = (rowB.original.phase || "").toLowerCase();
          return a.localeCompare(b);
        };
      case "Status":
        return (rowA, rowB, columnId) => {
          const a = rowA.original.status || 0;
          const b = rowB.original.status || 0;
          return a - b;
        };
      case "Priority":
        return (rowA, rowB, columnId) => {
          const a = rowA.original.priority || 0;
          const b = rowB.original.priority || 0;
          return a - b;
        };
      case "Milestone":
        return (rowA, rowB, columnId) => {
          const a = rowA.original.isMilestone ? 1 : 0;
          const b = rowB.original.isMilestone ? 1 : 0;
          return b - a;
        };
      case "In WSR":
        return (rowA, rowB, columnId) => {
          const a = rowA.original.isInWsr ? 1 : 0;
          const b = rowB.original.isInWsr ? 1 : 0;
          return b - a;
        };
      case "Budget":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.budget) || 0;
          const b = parseFloat(rowB.original.budget) || 0;
          return a - b;
        };
      case "Actual":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.actual) || 0;
          const b = parseFloat(rowB.original.actual) || 0;
          return a - b;
        };
      case "Accural":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.accural) || 0;
          const b = parseFloat(rowB.original.accural) || 0;
          return a - b;
        };
      case "ETC":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.etc) || 0;
          const b = parseFloat(rowB.original.etc) || 0;
          return a - b;
        };
      case "BAC":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.bac) || 0;
          const b = parseFloat(rowB.original.bac) || 0;
          return a - b;
        };
      case "Tasks":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.tasksCount) || 0;
          const b = parseFloat(rowB.original.tasksCount) || 0;
          return a - b;
        };
      case "Start Date":
        return (rowA, rowB, columnId) => {
          const a = new Date(rowA.original.startDate || 0);
          const b = new Date(rowB.original.startDate || 0);
          return a.getTime() - b.getTime();
        };
      case "End Date":
        return (rowA, rowB, columnId) => {
          const a = new Date(rowA.original.endDate || 0);
          const b = new Date(rowB.original.endDate || 0);
          return a.getTime() - b.getTime();
        };
      case "%":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.progress) || 0;
          const b = parseFloat(rowB.original.progress) || 0;
          return a - b;
        };
      case "Resources":
        return (rowA, rowB, columnId) => {
          const a = rowA.original.allocations?.length || 0;
          const b = rowB.original.allocations?.length || 0;
          return a - b;
        };
      default:
        return "alphanumeric";
    }
  };

  const getSortDescFirst = (col) => {
    const descFirstColumns = [
      "Milestone",
      "In WSR",
      "Priority",
      "Budget",
      "Actual",
      "Accural",
      "ETC",
      "BAC",
      "Tasks",
      "Start Date",
      "End Date",
      "%",
      "Resources"
    ];
    return descFirstColumns.includes(col.label);
  };


  const handleScopeDuplicate = (e, item, isIncludeTasks = false) => {
    e.stopPropagation();
    e.preventDefault();
    handlePopoverChange(
      item.id,
      !popoverStates[item.id]
    );

    if (handleAction) {
      handleAction({
        actionType: TABLE_ACTIONS.DUPLICATE,
        payload: {
          id: item.id,
          name: item.name,
          isIncludeTasks: isIncludeTasks
        }
      });
    }
  };

  useEffect(() => {
    if (!colsList?.length && !rowData.length) return;
    const columnsOrder = colsList
      .filter((col) => col.isVisible !== false)
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((item) => item.accessorKey);
    setColumnOrder(["mrt-row-expand", ...columnsOrder]);
    const columns = colsList
      .filter((col) => col.isVisible !== false)
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((col, index) => {
        return {
          Label: col.label,
          CustomName: col.customName,
          Order: col.order,
          Visibility: col.isVisible,
          accessorKey: col.accessorKey,
          header: col.header,
          enableHiding: true,
          enableColumnActions: false,
          grow: true,
          enableResizing: true,
          size: col.size,
          editVariant: col.columnType,
          enableGlobalFilter: true,
          enableEditing: (row) => {
            const rowType = row.original.rowType || ROW_TYPE.SCOPE;
            if (rowType !== ROW_TYPE.SCOPE) return false;
            return !(
              col.accessorKey === "tasksCount" ||
              col.accessorKey === "actual" ||
              col.accessorKey === "accural" ||
              col.accessorKey === "etc" ||
              col.accessorKey === "bac" ||
              col.accessorKey === "progress"
            );
          },
          enableSorting: editingHeader === col.accessorKey ? false : true,
          sortingFn: getSortingFunction(col),
          sortDescFirst: getSortDescFirst(col),
          muiEditTextFieldProps: ({ cell, row }) => ({
            sx: {
              fontFamily: "Figtree-Regular",
              fontSize: "14px",
              padding: "0.5rem",
              maxHeight: "28px",
            },
            required: col.required || false,
            error: !!validationErrors?.[`${row.id}_${cell.column.id}`],
            helperText: validationErrors?.[`${row.id}_${cell.column.id}`],
            onFocus: () =>
              setValidationErrors({
                ...validationErrors,
                [cell.column.id]: undefined,
              }),
            inputRef: (el) => {
              if (col.columnType === "select") {
                selectRefs.current[`${row.id}_${cell.column.id}`] = el;
              }
            },
            onChange: (event) => {
              const newValue = event.target.value;
              setEditedRows((prev) => ({
                ...prev,
                [row.id]: {
                  ...prev[row.id],
                  [cell.column.id]: newValue,
                },
              }));
            },
            onBlur: (event) => {
              const value = event.target.value;
              const validationError =
                col.required && !value ? "Required" : undefined;
              setValidationErrors((prev) => ({
                ...prev,
                [`${row.id}_${cell.column.id}`]: validationError,
              }));
            },
          }),
          Cell: ({ cell, row }) => {
            const item = row.original;
            if (item.isSkeleton) {
              return (
                <div
                  style={{
                    height: '20px',
                    background: 'linear-gradient(90deg, #e0e0e0 25%, #ECE7FD 50%, #e0e0e0 75%)',
                    backgroundSize: '200% 100%',
                    animation: 'shimmer 1.5s infinite',
                    borderRadius: '4px',
                    width: '100%'
                  }}
                />
              );
            }
            const rowType = item.rowType || ROW_TYPE.SCOPE;
            switch (col.label) {
              case "Scope":
                return (
                  <div
                    className="d-flex justify-content-between align-items-center inputWidth"
                    style={rowType === ROW_TYPE.TASK && item.taskGroup == null ? { position: 'relative', left: '-16px' } : { paddingLeft: (row.depth || 0) * 15 }}
                  >
                    <div className="d-flex align-items-center inputWidth">
                      {rowType === ROW_TYPE.TASK && item.taskGroup == null && (
                        <span
                          onClick={(e) => e.stopPropagation()}
                        >
                          <CheckboxInput
                            checked={selectedTasks.has(item.id)}
                            setChecked={(checked) => toggleTaskSelection(item, checked)}
                          />
                        </span>
                      )}
                      <Tooltip
                        positioning="before"
                        content={{
                          children: item.isFlagged ? "Unflag" : "Flag",
                          className: styles.tooltip,
                        }}
                        relationship="label"
                        withArrow
                      >
                        <span
                          className={`flag ${item.isFlagged ? "flagActive" : ""
                            }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            if (rowType === ROW_TYPE.SCOPE) {
                              handleFlag(item.id, !item.isFlagged);
                            } else if (rowType === ROW_TYPE.TASK) {
                              handleTaskFlag(item, !item.isFlagged);
                            } else if (rowType === ROW_TYPE.TASK_GROUP) {
                              handleTaskGroupFlag(item, !item.isFlagged);
                            }
                          }}
                        >
                          <FlagIcon />
                        </span>
                      </Tooltip>
                      <>
                        <span className="mr-2 min-w-[16px]">
                          {(rowType === ROW_TYPE.TASK_GROUP
                            ? (item.subRows || []).some(hasOpenRisk)
                            : hasOpenRisk(item)) && (
                              <Tooltip content="Includes risks that may affect delivery" relationship="label">
                                <TriangleAlert size={16} color="#d1424f" />
                              </Tooltip>
                            )}
                        </span>
                        <span
                          className="d-flex w-full text-truncate cursor-pointer"
                          onClick={() => {
                            if (rowType === ROW_TYPE.TASK) handleTaskEdit(item);
                            else if (rowType === ROW_TYPE.TASK_GROUP) navigateTo("TaskGroups", item.id, item.name);
                            else handleRedirect(item);
                          }}
                        >
                          {item.attachementsCount > 0 && (
                            <div className="mr-1">
                              <AttachementIcon count={item.attachementsCount} />
                            </div>
                          )}
                          {item.isPendingDelete && (
                            <div className="mr-1">
                              <PendingDeleteIcon />
                            </div>
                          )}
                          {item.name}
                        </span>
                      </>
                      <div className="meatballs-trigger">
                        <Popover
                          open={popoverStates[item.id]}
                          onOpenChange={(e, data) => handlePopoverChange(item.id, data.open || false)}
                          positioning="below-start"
                        >
                          <PopoverTrigger disableButtonEnhancement>
                            <TableCellLayout
                              media={<MeatBallsMenuIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                handlePopoverChange(
                                  item.id,
                                  !popoverStates[item.id]
                                );
                              }}
                            />
                          </PopoverTrigger>
                          <PopoverSurface
                            tabIndex={-1}
                            className="listingPopoverWrapper"
                          >
                            <div className="listingPopoverContent">
                              {rowType === ROW_TYPE.SCOPE && (
                                <>
                                  {getEditPermission("Scope") && (
                                    <div
                                      className="popItem"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        handleEdit(item);
                                        handlePopoverChange(
                                          item.id,
                                          !popoverStates[item.id]
                                        );
                                      }}
                                    >
                                      <EditIcon />
                                      <p className="text-sm-400 ms-12">Edit</p>
                                    </div>
                                  )}
                                  {getCreatePermission("Scope") && (
                                    item.tasksCount === 0 ? (
                                      <div
                                        className="popItem"
                                        onClick={(e) => { handleScopeDuplicate(e, item) }}
                                      >
                                        <Copy size={16} />
                                        <p className="text-sm-400 ms-12">Duplicate</p>
                                      </div>
                                    ) : (
                                      <Popover positioning="after-top" trapFocus>
                                        <PopoverTrigger disableButtonEnhancement>
                                          <div className="popItem">
                                            <Copy size={16} />
                                            <p className="text-sm-400 ms-12">Duplicate</p>
                                          </div>
                                        </PopoverTrigger>
                                        <PopoverSurface className="listingPopoverWrapper">
                                          <div className="listingPopoverContent">
                                            <div
                                              className="popItem"
                                              onClick={(e) => { handleScopeDuplicate(e, item) }}
                                            >
                                              <Target size={16} />
                                              <p className="text-sm-400 ms-12">{"Scope"}</p>
                                            </div>
                                            <div
                                              className="popItem"
                                              onClick={(e) => { handleScopeDuplicate(e, item, true) }}
                                            >
                                              <Layers2 size={16} />
                                              <p className="text-sm-400 ms-12">{"Scope & tasks"}</p>
                                            </div>
                                          </div>
                                        </PopoverSurface>
                                      </Popover>
                                    )
                                  )}
                                  {getCreatePermission("Task") && (
                                    <div
                                      className="popItem"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        handlePopoverChange(item.id, !popoverStates[item.id]);
                                        handleScopeAddTaskGroup(item);
                                      }}
                                    >
                                      <Plus size={16} />
                                      <p className="text-sm-400 ms-12">Add Task Group</p>
                                    </div>
                                  )}
                                  {getCreatePermission("Task") && (
                                    <div
                                      className="popItem"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        handlePopoverChange(item.id, !popoverStates[item.id]);
                                        handleScopeAddTask(item);
                                      }}
                                    >
                                      <Plus size={16} />
                                      <p className="text-sm-400 ms-12">Add Task</p>
                                    </div>
                                  )}
                                  <div
                                    className="popItem"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      handleReminder(item);
                                      handlePopoverChange(
                                        item.id,
                                        !popoverStates[item.id]
                                      );
                                    }}
                                  >
                                    <RemindIcon />
                                    <p className="text-sm-400 ms-12">Remind me</p>
                                  </div>
                                  {getDeletePermission("Scope") && (
                                    <div
                                      className="popItem"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        handlePopoverChange(
                                          item.id,
                                          !popoverStates[item.id]
                                        );
                                        handleDelete(item);
                                      }}
                                    >
                                      <DeleteIcon />
                                      <p className="text-sm-400 ms-12">Delete</p>
                                    </div>
                                  )}
                                </>
                              )}
                              {rowType === ROW_TYPE.TASK_GROUP && (
                                <>
                                  <div
                                    className="popItem"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      handlePopoverChange(item.id, !popoverStates[item.id]);
                                      handleTaskGroupEdit(item);
                                    }}
                                  >
                                    <EditIcon />
                                    <p className="text-sm-400 ms-12">Edit</p>
                                  </div>
                                  {getCreatePermission("Task") && (
                                    <div
                                      className="popItem"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        handlePopoverChange(item.id, !popoverStates[item.id]);
                                        handleTaskGroupAddTask(item);
                                      }}
                                    >
                                      <Plus size={16} />
                                      <p className="text-sm-400 ms-12">Add Task</p>
                                    </div>
                                  )}
                                  {getCreatePermission("Task") && (
                                    item.tasksCount === 0 ? (
                                      <div
                                        className="popItem"
                                        onClick={(e) => { handleTaskGroupDuplicate(e, item) }}
                                      >
                                        <Copy size={16} />
                                        <p className="text-sm-400 ms-12">Duplicate</p>
                                      </div>
                                    ) : (
                                      <Popover positioning="after-top" trapFocus>
                                        <PopoverTrigger disableButtonEnhancement>
                                          <div className="popItem">
                                            <Copy size={16} />
                                            <p className="text-sm-400 ms-12">Duplicate</p>
                                          </div>
                                        </PopoverTrigger>
                                        <PopoverSurface className="listingPopoverWrapper">
                                          <div className="listingPopoverContent">
                                            <div
                                              className="popItem"
                                              onClick={(e) => { handleTaskGroupDuplicate(e, item) }}
                                            >
                                              <Target size={16} />
                                              <p className="text-sm-400 ms-12">{"Group"}</p>
                                            </div>
                                            <div
                                              className="popItem"
                                              onClick={(e) => { handleTaskGroupDuplicate(e, item, true) }}
                                            >
                                              <Layers2 size={16} />
                                              <p className="text-sm-400 ms-12">{"Group & tasks"}</p>
                                            </div>
                                          </div>
                                        </PopoverSurface>
                                      </Popover>
                                    )
                                  )}
                                  <div
                                    className="popItem"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      handlePopoverChange(item.id, !popoverStates[item.id]);
                                      setTaskGroupData(item);
                                      setIsDeleteTaskGroupModal(true);
                                    }}
                                  >
                                    <DeleteIcon />
                                    <p className="text-sm-400 ms-12">Delete</p>
                                  </div>
                                </>
                              )}
                              {rowType === ROW_TYPE.TASK && (
                                <>
                                  <div
                                    className="popItem"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      handlePopoverChange(item.id, !popoverStates[item.id]);
                                      handleTaskEdit(item);
                                    }}
                                  >
                                    <EditIcon />
                                    <p className="text-sm-400 ms-12">Edit</p>
                                  </div>
                                  {getCreatePermission("Task") && (
                                    <div
                                      className="popItem"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        handlePopoverChange(item.id, !popoverStates[item.id]);
                                        setTaskData(item);
                                        setIsDuplicateTaskModal(true);
                                      }}
                                    >
                                      <Copy size={16} />
                                      <p className="text-sm-400 ms-12">Duplicate</p>
                                    </div>
                                  )}
                                  <div
                                    className="popItem"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      handlePopoverChange(item.id, !popoverStates[item.id]);
                                      setTaskData(item);
                                      setIsReminderTaskModal(true);
                                    }}
                                  >
                                    <RemindIcon />
                                    <p className="text-sm-400 ms-12">Remind me</p>
                                  </div>
                                  <div
                                    className="popItem"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      handlePopoverChange(item.id, !popoverStates[item.id]);
                                      setTaskData(item);
                                      setIsDeleteTaskModal(true);
                                    }}
                                  >
                                    <DeleteIcon />
                                    <p className="text-sm-400 ms-12">Delete</p>
                                  </div>
                                </>
                              )}
                            </div>
                          </PopoverSurface>
                        </Popover>
                      </div>
                    </div>
                  </div>
                );
              case "Sequence #":
                return GetItemSequence(rowType, item.sequenceNumber);
              case "Project":
                if (rowType !== ROW_TYPE.SCOPE) return null;
                return (
                  <span onClick={() => handleProjectRedirect(item)}>
                    {item.project ? item.project.name : ""}
                  </span>
                );
              case "Phase":
                return item.phase;
              case "Status": {
                if (rowType === ROW_TYPE.TASK || rowType === ROW_TYPE.TASK_GROUP) {
                  return (
                    <div className="flex w-100 justify-center items-center">
                      <p className={`w-100 ${getTaskStatusPill(item.status)}`}>
                        {getTaskStatus(item.status)}
                      </p>
                    </div>
                  );
                }
                return (
                  <div className="flex w-100 justify-center items-center">
                    <p
                      className={`pill w-100 ${item.status === 0
                        ? "OnTrack"
                        : item.status === 1
                          ? "OffTrack"
                          : item.status === 2
                            ? "AtRisk"
                            : "Completed"
                        }`}
                    >
                      {getLabelByType("scope", item.status)}
                    </p>
                  </div>
                );
              }
              case "Priority":
                return (
                  <div className="flex w-100 justify-center items-center">
                    <p className={`${getPriorityPill(item.priority)} w-100`}>
                      {getPriority(item.priority)}
                    </p>
                  </div>
                );
              case "Milestone": {
                if (rowType === ROW_TYPE.TASK_GROUP) return null;
                return (

                  <div
                    className="flex w-100 justify-center items-center cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (rowType === ROW_TYPE.TASK) {
                        handleTaskToggleMilestone(item, !item.isMilestone);
                      } else {
                        handleToggleMilestone(item.id, !item.isMilestone);
                      }
                    }}
                  >

                    {item.isMilestone ? (
                      <MilestoneIcon size={20} />
                    ) : (
                      <Tooltip
                        positioning="before"
                        content={{
                          children: "Mark as Milestone",
                          className: styles.tooltip,
                        }}
                        relationship="label"
                        withArrow
                      >
                        <span className="milestone-inactive">
                          <UnActiveMilestoneIcon size={20} />
                        </span>
                      </Tooltip>
                    )}
                  </div >

                );
              }
              case "In WSR": {
                if (rowType === ROW_TYPE.TASK_GROUP) return null;
                return (
                  <div
                    className="flex w-100 justify-center items-center cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (rowType === ROW_TYPE.TASK) {
                        handleTaskToggleWsr(item, !item.isInWsr);
                      } else {
                        handleToggleField(item.id, "isInWsr", !item.isInWsr);
                      }
                    }}
                  >
                    {item.isInWsr ? (
                      <CheckmarkCircleIcon />
                    ) : (
                      <DismissCircleIcon />
                    )}
                  </div>
                );
              }
              case "Budget":
                if (rowType !== ROW_TYPE.SCOPE) return null;
                return `${getAccountCurrency(
                  setting
                )} ${displayDecimalFormatted(setting, item.budget)}`;
              case "Actual":
                if (rowType !== ROW_TYPE.SCOPE) return null;
                return item.actual
                  ? `${getAccountCurrency(setting)} ${displayDecimalFormatted(
                    setting,
                    item.actual
                  )}`
                  : "";
              case "Accural":
                if (rowType !== ROW_TYPE.SCOPE) return null;
                return item.accural
                  ? `${getAccountCurrency(setting)} ${displayDecimalFormatted(
                    setting,
                    item.accural
                  )}`
                  : "";
              case "ETC":
                if (rowType !== ROW_TYPE.SCOPE) return null;
                return item.etc
                  ? `${getAccountCurrency(setting)} ${displayDecimalFormatted(
                    setting,
                    item.etc
                  )}`
                  : "";
              case "Tasks":
                if (rowType === ROW_TYPE.TASK) return null;
                return `${displayDecimalFormatted(setting, item.tasksCount)}`;
              case "Start Date":
                return item.startDate
                  ? dateOnlyFormatted(setting, item.startDate)
                  : "";
              case "End Date":
                return item.endDate
                  ? dateOnlyFormatted(setting, item.endDate)
                  : "";
              case "%":
                return item.progress ? `${item.progress}%` : "";
              case "Resources":
                return (
                  <div className="w-full flex justify-center">
                    {getResources(rowType, item)}
                  </div>
                );

              default:
                return cell.getValue();
            }
          },
          Edit: ({ cell, row, table }) => {
            const value =
              editedRows[row.id]?.[cell.column.id] ??
              cell.getValue() ??
              row.original[cell.column.id] ??
              (col.columnType === "select" ? null : "");
            return (
              <EditInput
                label={col.label}
                value={value}
                onChange={(newValue) => {
                  if (row.id.startsWith("mrt-row-create")) {
                    table.setCreatingRow((prev) => ({
                      ...prev,
                      values: {
                        ...prev.values,
                        [cell.column.id]: newValue,
                      },
                    }));
                  } else {
                    setEditedRows((prev) => ({
                      ...prev,
                      [row.id]: {
                        ...prev[row.id],
                        [cell.column.id]: newValue,
                      },
                    }));
                  }
                }}
                onBlur={() => {
                  if (!row.id.startsWith("mrt-row-create")) {
                    const newValue = editedRows[row.id]?.[cell.column.id];
                    table.options.onEditingCellSave?.({
                      cell,
                      value: newValue,
                      row,
                      table,
                    });
                    table.setEditingCell(null);
                  }
                }}
                type={col.columnType}
                dateFormat={
                  col.columnType === "date"
                    ? setting.regionalSettings.dateFormat
                    : null
                }
                clientId={row.original?.client?.id}
                portfolioId={row.original?.portfolio?.id}
                programId={row.original?.program?.id}
                parentType={parentType}
                table={table}
              />
            );
          },
          Header: ({ column }) => {
            return editingHeader === column.id ? (
              <input
                value={headerValue}
                onChange={(e) => setHeaderValue(e.target.value)}
                onBlur={() => handleHeaderSave(column.id)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleHeaderSave(column.id);
                  }
                }}
                autoFocus
                className="header-edit-input"
                style={{
                  minWidth: column.getSize() - 60,
                }}
              />
            ) : (
              <span>{column.columnDef.header}</span>
            );
          },
        };
      });
    setCols(columns);

    setRows(() => {
      if (!rowData || !Array.isArray(rowData) || rowData.length === 0) {
        return [];
      }
      let baseRows = rowData;
      if (isMilestones) {
        baseRows = rowData.filter((item) => item.isMilestone);
      }
      baseRows = [...baseRows].sort(compareByFlagAndStartDate);

      if (isFetchingMore && pagination?.HasNext) {
        const skeletonRows = createSkeletonRows();
        return [...baseRows, ...skeletonRows];
      }

      return baseRows;
    });
  }, [colsList, rowData, editingHeader, headerValue, isFetchingMore, pagination?.HasNext, isMilestones, selectedTasks]);

  // Scope-level create/edit/delete/duplicate happen outside this component
  // (Redux, see ScopeItem.jsx) and only ever update the `rowData` prop. While
  // a search is active, treeData renders searchTreeRows exclusively (see
  // below), so re-run the active search whenever rowData changes to keep
  // search results in sync with those mutations too.
  useEffect(() => {
    if (activeSearchTerm) {
      handleTriggerSearch(activeSearchTerm);
    }

  }, [rowData]);

  // Merge lazily-fetched per-scope children into the rows fed to the table,
  // without disturbing the effect above (which only tracks the paginated
  // top-level scope list). While a search is active, its self-contained,
  // already-expanded rows replace this entirely (see handleTriggerSearch).
  const treeData = useMemo(() => {
    if (searchTreeRows) return searchTreeRows;
    if (isSearching) return createSkeletonRows();

    return rows.map((r) => {
      if (scopeChildren[r.id]) {
        return { ...r, subRows: scopeChildren[r.id] };
      }
      if (loadingScopeChildrenIds.has(r.id)) {
        return { ...r, subRows: createChildSkeletonRows(r.id) };
      }
      return r;
    });
  }, [rows, scopeChildren, loadingScopeChildrenIds, searchTreeRows, isSearching]);

  // While "expand all" is active, keep cascading the expanded map as each
  // scope's task groups stream in so nested groups open automatically too.
  useEffect(() => {
    if (!isAllRowsExpanded) return;
    const next = {};
    rows.forEach((r) => {
      next[r.id] = true;
    });
    Object.entries(scopeChildren).forEach(([scopeId, children]) => {
      next[scopeId] = true;
      children.forEach((child) => {
        if (child.rowType === ROW_TYPE.TASK_GROUP) next[child.id] = true;
      });
    });
    setExpandedRows(next);
  }, [isAllRowsExpanded, scopeChildren, rows]);

  // Search results are already pruned to "everything relevant" — force every
  // scope and task-group row open so nothing needs a manual click to reveal.
  useEffect(() => {
    if (!searchTreeRows) return;
    const next = {};
    searchTreeRows.forEach((scopeRow) => {
      next[scopeRow.id] = true;
      (scopeRow.subRows || []).forEach((child) => {
        if (child.rowType === ROW_TYPE.TASK_GROUP) next[child.id] = true;
      });
    });
    setExpandedRows(next);
  }, [searchTreeRows]);

  const table = useMaterialReactTable({
    columns: cols,
    data: treeData,
    getRowId: (row) => row.id,
    getSubRows: (row) => row.subRows,
    ...(!isMilestones && {
      enableExpanding: (row) => true,
    }),
    ...(!isMilestones && {
      getRowCanExpand: (row) => true,
    }),
    initialState: {
      expanded: expandedRows,
      density: "compact",
      showGlobalFilter: false,
      showColumnFilters: false,
      enableHiding: false,
      columnOrder,
    },
    state: {
      sorting,
      isLoading: rows.length === 0 && scopeLoading,
      columnSizing,
      columnOrder,
      showLoadingOverlay: false,
      showProgressBars: false,
      expanded: expandedRows,
    },
    enableHiding: true,
    enableSorting: true,
    manualSorting: false,
    onSortingChange: setSorting,
    visibleInShowHideMenu: false,
    enablePagination: false,
    enableTopToolbar: true,
    enableGlobalFilter: true,
    enableStickyHeader: true,
    enableColumnFilterModes: true,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    editDisplayMode: "cell",
    globalFilterFn: "contains",
    enableEditing: false,
    enableFilters: false,
    enableCreatingRow: true,
    createDisplayMode: "row",
    positionCreatingRow: "bottom",
    muiExpandButtonProps: ({ row }) => {
      const rowType = row.original.rowType || ROW_TYPE.SCOPE;
      return {
        onClick: (e) => {
          e.stopPropagation();
          const isCurrentlyExpanded = expandedRows[row.id];
          if (!isCurrentlyExpanded && rowType === ROW_TYPE.SCOPE && !searchTreeRows) {
            loadScopeChildren(row.original.id);
          }
          setExpandedRows((prev) => ({
            ...prev,
            [row.id]: !isCurrentlyExpanded,
          }));
        },
        sx: {
          visibility: rowType === ROW_TYPE.TASK ? "hidden" : "visible",
        },
      };
    },
    muiExpandAllButtonProps: () => ({
      onClick: (e) => {
        e.stopPropagation();
        if (searchTreeRows) return;
        if (isAllRowsExpanded) {
          setExpandedRows({});
          setIsAllRowsExpanded(false);
          return;
        }
        setIsAllRowsExpanded(true);
        loadAllScopeChildren();
      },
    }),
    onCreatingRowCancel: () => {
      setValidationErrors({});
      handleCancelAddRow();
    },
    onCreatingRowSave: ({ values, table }) => {
      const errors = {};
      if (
        values?.startDate &&
        values?.endDate &&
        isDateGreaterThan(values?.startDate, values?.endDate)
      ) {
        errors[
          "endDate"
        ] = `End date must be equal to or greater than start date.`;
      }
      if (
        values?.startDate &&
        values?.revisedEndDate &&
        isDateGreaterThan(values?.startDate, values?.revisedEndDate)
      ) {
        errors[
          "revisedEndDate"
        ] = `Revised end date must be equal to or greater than start date.`;
      }
      if (!values?.name) errors["name"] = `The Name field is required.`;

      if (!projectId) {
        if (!projectId) errors["projectId"] = `The Project field is required.`;
      }

      if (Object.keys(errors).length) {
        dispatch(
          generalActions.showToast({
            title:
              errors.name ||
              errors.projectId ||
              errors.endDate ||
              errors.revisedEndDate,
            type: "error",
          })
        );
        return;
      }

      const payload = {
        Name: values?.name,
        ProjectId: projectId ? projectId : "",
        StartDate: values?.startDate ? values?.startDate : null,
        EndDate: values?.endDate ? values?.endDate : null,
        RevisedEndDate: values?.revisedEndDate ? values?.revisedEndDate : null,
        Priority: setRequestPriority(values?.priority),
        Budget: values?.budget ? values?.budget : 0,
        Phase: values?.phase ?? "",
        Status: values?.status ?? 0,
        RefId: values?.refId ?? "",
        Description: values?.description ?? "",
        AssignedUsers: [],
        IsMilestone: false,
      };
      handleCreate(payload);
      table.setCreatingRow(null);
    },
    onEditingRowCancel: () => {
      setValidationErrors({});
      setEditedRows({});
    },
    positionGlobalFilter: "left",
    onEditingCellSave: ({ cell, row }) => {
      const editedRow = editedRows[row.id];
      const newValue =
        editedRow?.[cell.column.id] !== undefined
          ? editedRow[cell.column.id]
          : cell.getValue();

      if (newValue === cell.getValue()) return; // Skip if no real change
      setRows((prevRows) =>
        prevRows.map((r, index) => {
          if (index === row.index) {
            if (cell.column.id === "status") {
              return {
                ...r,
                status: ScopeStatus.find((item) => item.name === newValue.name)
                  ?.value,
              };
            } else if (cell.column.id === "priority") {
              return { ...r, priority: setRequestPriority(newValue.name) };
            }
            return { ...r, [cell.column.id]: newValue };
          }
          return r;
        })
      );

      // Remove the specific cell edit
      setEditedRows((prev) => {
        const updated = { ...prev };
        if (updated[row.id]) {
          delete updated[row.id][cell.column.id];
          if (Object.keys(updated[row.id]).length === 0) {
            delete updated[row.id];
          }
        }
        return updated;
      });

      const prePayload =
        cell.column.id === "owner"
          ? {
            ...row.original,
            OwnerId: newValue?.id,
          }
          : cell.column.id === "status"
            ? {
              ...row.original,
              status: ScopeStatus.find((item) => item.name === newValue.name)
                ?.value,
            }
            : cell.column.id === "priority"
              ? {
                ...row.original,
                priority: setRequestPriority(newValue.name),
              }
              : {
                ...row.original,
                [cell.column.id]: newValue,
              };
      const payload = formatPayload(prePayload);
      handleSave(row.original.id, {
        ...payload,
        ClientId: payload.client.id,
        ProjectId: payload.project.id,
      });
    },
    onColumnSizingChange: (updater) => {
      setColumnSizing((prev) => {
        const newSizing =
          typeof updater === "function" ? updater(prev) : updater;
        Object.entries(newSizing).forEach(([columnId, width]) => {
          setColumnWidth(tableName, columnId, width);
        });
        return newSizing;
      });
    },
    onColumnOrderChange: (updater) => {
      const newOrder = typeof updater === "function" ? updater() : updater;
      const neworderList = newOrder
        .filter((item) => item !== "mrt-row-spacer")
        .filter(Boolean);
      const areEqual = compareArrays(
        neworderList
          .filter((item) => item !== "mrt-row-expand")
          .filter(Boolean),
        columnOrder.filter((item) => item !== "mrt-row-expand")
      );
      if (areEqual) {
        return neworderList;
      }

      const sortedColumns = sortColumnsByOrderlist(colsList, neworderList);
      handleColumnOrdering(sortedColumns);
      setColumnOrder(neworderList);
      return neworderList;
    },
    muiTableHeadCellProps: ({ column }) => ({
      sx: {
        fontFamily: "Figtree-Medium",
        fontSize: "14px",
        padding: "1rem 0.5rem 1rem 1rem",
        backgroundColor: "#f8f7fc",
        color: "#342c82",
        height: "40px",
        zIndex: 100,
        fontWeight: 600,
        "&:hover": {
          backgroundColor: "#ece8fd",
          "& .MuiDivider-root": {
            opacity: 1,
          },
          "& .MuiButtonBase-root": {
            opacity: 1,
          },
          fontWeight: 700,
        },
        position: "relative",
        "& .Mui-TableHeadCell-Content": {
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          height: "100%",
        },
        "& .MuiTableSortLabel-root": {
          marginLeft: "8px",
          paddingTop: "3px",
          opacity: 0,
        },
        "& .MuiDivider-root": {
          cursor: "col-resize",
          backgroundColor: "#342C82",
          opacity: 0,
        },
        "& .MuiButtonBase-root": {
          cursor: "grab",
          opacity: 0,
          "& .MuiSvgIcon-root": {
            path: {
              fill: "#342C82"
            }
          }
        },
        ...(column.id === "mrt-row-expand" && {
          maxWidth: "45px",
          width: "45px",
          minWidth: "45px",
          padding: "1rem 0.5rem ",
        }),
      },
      onDoubleClick: (e) => {
        e.stopPropagation();
        e.preventDefault();
        handleHeaderDoubleClick(column.id, column.columnDef.header);
      },
    }),
    muiTopToolbarProps: {
      showColumnFilters: false,
      showGlobalFilter: true,
      enableHiding: false,
      sx: {
        position: "sticky",
        top: 0,
        zIndex: 1,
        "& .MuiBox-root": {
          padding: 0,
          display: "flex",
          alignItems: "center",
        },
      },
    },
    renderTopToolbar: () => (
      <ListingHeader
        isHeader={isHeader}
        parentType={parentType}
        type="Scope"
        btnLable={!isMilestones ? "Add Scope" : "Add Milestone"}
        searchedData={searchHandler}
        setIsCreate={() => setIsCreate(true)}
        rightPane={true}
        portfolioId={portfolioId}
        programId={programId}
        projectId={projectId}
        isCreatable={true}
        isSearchDisable={cols.length}
        handleCreateFunc={() => {
          handleAddRow();
          table.setCreatingRow(true);
        }}
        triggerSearch={handleTriggerSearch}
        secondaryAction={
          !isMilestones && getCreatePermission("Task")
            ? { label: "Add Task Group", onClick: handleTaskGroupCreate }
            : undefined
        }
        selectionAction={
          canGroupSelectedTasks
            ? {
              label: `Group ${selectedTaskList.length} Task${selectedTaskList.length > 1 ? "s" : ""}`,
              onClick: handleGroupSelectedTasks,
            }
            : undefined
        }
      />
    ),
    renderBottomToolbarCustomActions: () => (
      <div className="bottom-toolbar-container">
        <div className="toolbar-content">
          {scopeLoading || isFetchingMore ? (
            <div className="loading-indicator">
              <span>{`${!isFetchingMore ? 'Loading...' : 'Loading more data...'}`}</span>
            </div>
          ) : pagination?.TotalCount > 0 ? (
            <div className="data-info">
              <span className="data-text">
                Showing <strong>{rows.length}</strong> of{" "}
                <strong>{pagination?.TotalCount || 0}</strong> total records
              </span>
            </div>
          ) : (
            <div className="data-info">
              <span className="data-text">No records Found</span>
            </div>
          )}
        </div>
      </div>
    ),
    muiBottomToolbarProps: {
      sx: {
        position: "absolute",
        bottom: 0,
        backgroundColor: "#f8f7fc",
        padding: "0.7rem 1rem",
        width: "100%",
        height: "38px",
      },
    },
    muiTableBodyProps: {
      sx: {
        fontFamily: "Figtree-Regular",
        fontSize: "13px",

        marginBottom: "66px",
        "& .MuiTableBody-Cell": {
          "&:hover": {
            backgroundColor: "#ece8fd15",
          },
        },
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        ...(expandedRows[row.id]
          ? {
            backgroundColor: "#ece8fd",
            "& td": {
              verticalAlign: "top",
              backgroundColor: "#transparent !important",
              fontWeight: "bold",
            },
          }
          : {
            "& td": {
              backgroundColor: "transparent",
            },
          }),
        ...(isMilestones && { height: "32px" }),
        ...(row.original.isSkeleton && {
          "&:hover": {
            backgroundColor: "transparent",
          },
          cursor: "default",
        }),
      },
      className: row.original.isSkeleton ? "" : "hoverable-row",
    }),
    muiDetailPanelProps: () => ({
      sx: {
        backgroundColor: "#f8f9fa",
        borderTop: "1px solid #dee2e6",
        "& td": {
          padding: 0,
        },
      },
    }),
    muiTableContainerProps: ({ table }) => ({
      ref: tableContainerRef,
      sx: {
        height: `calc(100% - ${table.refs.topToolbarRef.current?.offsetHeight || 115
          }px)`,
        overflow: "auto",
        "&::-webkit-scrollbar": {
          height: "8px",
          width: "8px",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "#FFFFFF",
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#342c82",
          borderRadius: "4px",
          "&:hover": {
            backgroundColor: "#342c82",
          },
        },
      },
    }),
    muiTablePaperProps: {
      sx: {
        height: "100%",
      },
    },
    muiSkeletonProps: {
      height: 34,
      sx: {
        background:
          "linear-gradient(90deg, #e0e0e0 25%, #ECE7FD 50%, #e0e0e0 75%)",
        backgroundSize: "200% 100%",
        animation: "shimmer 1.5s infinite",
        "@keyframes shimmer": {
          "0%": {
            backgroundPosition: "200% 0",
          },
          "100%": {
            backgroundPosition: "-200% 0",
          },
        },
      },
    },
    muiTableBodyCellProps: ({ cell, column, table }) => ({
      sx: {
        fontFamily: "Figtree-Regular",
        fontSize: "13px",
        padding: "0px 1rem",
        position: "relative",
        overflow: "visible",
        "&:focus": {
          outline: "none",
        },
        "&:hover": {
          fontFamily: "Figtree-Medium",
          fontWeight: "bold",
        },
        ...(column.id === "mrt-row-expand" && {
          maxWidth: "45px",
          width: "45px",
          minWidth: "45px",
          padding: "0px 0.5rem",
        }),
      },
    }),
    renderEmptyRowsFallback: () => {
      return scopeLoading ? (
        <div className="d-flex justify-content-center">
          <Spin />
        </div>
      ) : rows.length === 0 ? (
        <div className="flex-grow flex items-center justify-center p-4">
          <div className="h-full flex flex-col items-center justify-center min-h-[400px] p-8">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="65"
              height="64"
              viewBox="0 0 65 64"
              fill="none"
              className="mb-4"
            >
              <path
                d="M6.9002 21.6008C6.9002 16.7407 10.8401 12.8008 15.7002 12.8008H49.3002C54.1603 12.8008 58.1002 16.7407 58.1002 21.6008V42.4008C58.1002 47.2609 54.1603 51.2008 49.3002 51.2008H32.8884L27.8968 46.2092C28.7964 44.3293 29.3002 42.2239 29.3002 40.0008C29.3002 32.0479 22.8531 25.6008 14.9002 25.6008C11.94 25.6008 9.18835 26.494 6.9002 28.0257V21.6008ZM14.9002 51.2008C17.416 51.2008 19.7381 50.3713 21.6079 48.9708L29.7688 57.132C30.3936 57.7569 31.4067 57.7569 32.0315 57.1321C32.6564 56.5072 32.6564 55.4942 32.0316 54.8693L23.8706 46.708C25.2708 44.8384 26.1002 42.5164 26.1002 40.0008C26.1002 33.8152 21.0858 28.8008 14.9002 28.8008C8.71461 28.8008 3.7002 33.8152 3.7002 40.0008C3.7002 46.1864 8.71461 51.2008 14.9002 51.2008ZM14.9002 48.0008C10.4819 48.0008 6.9002 44.4191 6.9002 40.0008C6.9002 35.5825 10.4819 32.0008 14.9002 32.0008C19.3185 32.0008 22.9002 35.5825 22.9002 40.0008C22.9002 44.4191 19.3185 48.0008 14.9002 48.0008Z"
                fill="#342C82"
              />
            </svg>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              No data currently available!
            </h3>
            <p className="text-gray-600 text-center max-w-md">
              New entries will appear here once created.
            </p>
          </div>
        </div>
      ) : null;
    },
    muiLinearProgressProps: {
      sx: {
        height: "4px",
        backgroundColor: "#cfc9ff",
        "& .MuiLinearProgress-bar": {
          backgroundColor: "#342c82",
        },
        "& .MuiLinearProgress-bar1Indeterminate": {
          backgroundColor: "#342c82",
        },
        "& .MuiLinearProgress-bar2Indeterminate": {
          backgroundColor: "#342c82",
        },
        "& .MuiLinearProgress-barColorPrimary": {
          backgroundColor: "#342c82",
        },
        "& .MuiLinearProgress-colorPrimary": {
          backgroundColor: "#cfc9ff",
        },
        "& .MuiLinearProgress-colorSecondary": {
          backgroundColor: "#cfc9ff",
        },
        "& .MuiLinearProgress-barColorSecondary": {
          backgroundColor: "#342c82",
        },
      },
    },
  });

  return (
    <>
      <MaterialReactTable table={table} />

      {isTaskModalOpen && (
        <CreateEditTask
          task={isEditTaskModal ? taskData : null}
          isEdit={isEditTaskModal}
          scopeID={isEditTaskModal ? taskData?.__scopeId : createTaskContext?.scopeId}
          scopeName={isEditTaskModal ? taskData?.__scopeName : createTaskContext?.scopeName}
          lockScope={!isEditTaskModal}
          initialTaskGroup={!isEditTaskModal ? createTaskContext?.taskGroup : null}
          projectID={projectId}
          isOpen={isTaskModalOpen}
          setRowData={() => { }}
          onSave={() => {
            const targetScopeId = isEditTaskModal
              ? taskData?.__scopeId
              : createTaskContext?.scopeId;
            if (targetScopeId) refreshScopeTree(targetScopeId);
          }}
          onClose={closeTaskModal}
        />
      )}

      {isDeleteTaskModal && (
        <Modal
          title="Delete Task"
          body={
            <p className="text-sm-400 subHeading-color">{`Are you sure you want to delete the selected task?`}</p>
          }
          handleCancel={() => setIsDeleteTaskModal(false)}
          handleSave={() => handleTaskDeleteConfirm(taskData)}
          saveBtnLabel="Delete"
          cancelBtnLabel="Cancel"
          isLoading={isDeletingTask}
        />
      )}

      {isDuplicateTaskModal && (
        <Modal
          title="Duplication Confirmation"
          body={
            <p className="text-sm-400 subHeading-color">{`Are you sure you want to duplicate ${taskData?.name || "this item"}?`}</p>
          }
          handleCancel={() => setIsDuplicateTaskModal(false)}
          handleSave={() => handleTaskDuplicateConfirm(taskData)}
          saveBtnLabel="Confirm"
          cancelBtnLabel="Cancel"
          isLoading={isDuplicatingTask}
        />
      )}

      {isReminderTaskModal && (
        <CreateReminder
          setIsModalOpen={setIsReminderTaskModal}
          type="Task"
          selectedTag={taskData}
        />
      )}

      {isTaskGroupModalOpen && (
        <CreateEditTaskGroup
          taskGroup={taskGroupData}
          isEdit={isEditTaskGroup}
          scopeId={isEditTaskGroup ? taskGroupData?.__scopeId : groupTasksScopeId}
          requireScopePicker={!isEditTaskGroup && !groupTasksScopeId}
          initialTasks={!isEditTaskGroup ? groupTasksInitialTasks : []}
          portfolioId={portfolioId}
          programId={programId}
          projectId={projectId}
          isOpen={isTaskGroupModalOpen}
          onClose={closeTaskGroupModal}
          onSaved={(savedScopeId) => {
            if (savedScopeId) refreshScopeTree(savedScopeId);
            setSelectedTasks(new Map());
            closeTaskGroupModal();
          }}
        />
      )}

      {isDeleteTaskGroupModal && (
        <Modal
          title="Delete Task Group"
          body={
            <p className="text-sm-400 subHeading-color">
              {`Deleting "${taskGroupData?.name || "this task group"}" will also delete its grouped tasks. Are you sure you want to continue?`}
            </p>
          }
          handleCancel={() => {
            setIsDeleteTaskGroupModal(false);
            setTaskGroupData(null);
          }}
          handleSave={() => handleTaskGroupDeleteConfirm(taskGroupData)}
          saveBtnLabel="Delete"
          cancelBtnLabel="Cancel"
          isLoading={isDeletingTaskGroup}
        />
      )}

      {isDuplicateTaskGroupModal && (
        <Modal
          title="Duplication Confirmation"
          body={
            <p className="text-sm-400 subHeading-color">
              {`Are you sure you want to duplicate ${taskGroupData?.name || "this item"}${duplicateTaskGroupIncludeTasks ? " and its tasks" : ""}?`}
            </p>
          }
          handleCancel={() => {
            setIsDuplicateTaskGroupModal(false);
            setTaskGroupData(null);
          }}
          handleSave={handleTaskGroupDuplicateConfirm}
          saveBtnLabel="Confirm"
          cancelBtnLabel="Cancel"
          isLoading={isDuplicatingTaskGroup}
        />
      )}
    </>
  );
};

export default ScopeTable;
